// Admin — Auto-Approve Rules widget (visible from Smart Panel context)
import React, { useCallback, useEffect, useState } from 'react';
import { Zap, Plus, Trash2, Power } from 'lucide-react';
import { toast } from 'sonner';
import AdminShell from '../../components/admin/AdminShell';
import api from '../../utils/api';

export default function AdminAutoApproveRules() {
  const [rules, setRules] = useState([]);
  const [form, setForm] = useState({ kind: 'fund_deposit', max_amount: 500, min_past_success: 3, active: true, note: '' });

  const load = useCallback(async () => {
    try { const r = await api.get('/admin/auto-approve/rules'); setRules(r.data.rules || []); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Load failed'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.max_amount || form.max_amount <= 0) { toast.error('Max amount must be > 0'); return; }
    try {
      await api.post('/admin/auto-approve/rules', form);
      toast.success('✅ Rule created — will auto-approve matching requests');
      setForm({ ...form, max_amount: 500, note: '' });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Create failed'); }
  };

  const toggle = async (id, current) => {
    try { await api.patch(`/admin/auto-approve/rules/${id}?active=${!current}`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Toggle failed'); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this rule?')) return;
    try { await api.delete(`/admin/auto-approve/rules/${id}`); toast.success('✅ Deleted'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Delete failed'); }
  };

  return (
    <AdminShell>
      <div className="p-4 md:p-6 max-w-4xl mx-auto space-y-4" data-testid="admin-auto-approve">
        <div>
          <h1 className="text-2xl md:text-3xl font-black flex items-center gap-2"><Zap size={24} className="text-yellow-400" /> Auto-Approve Rules</h1>
          <p className="text-xs opacity-70">Auto-approve routine requests from trusted users · Save admin time</p>
        </div>

        <div className="rounded-2xl bg-gradient-to-r from-yellow-900/50 to-amber-900/50 border border-yellow-400/40 p-4 space-y-2" data-testid="auto-approve-form">
          <div className="text-sm font-black flex items-center gap-1"><Plus size={14} /> New Rule</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <label className="text-[10px] font-black opacity-80">Kind
              <select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} data-testid="rule-kind"
                      className="w-full mt-0.5 px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-sm">
                <option value="fund_deposit">💰 Fund Deposits</option>
                <option value="at_deposit">🪙 AT Deposits</option>
              </select>
            </label>
            <label className="text-[10px] font-black opacity-80">Max Amount (₹)
              <input type="number" value={form.max_amount} onChange={(e) => setForm({ ...form, max_amount: Number(e.target.value) })} data-testid="rule-max-amount"
                     className="w-full mt-0.5 px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-sm" />
            </label>
            <label className="text-[10px] font-black opacity-80">Min Past Approvals
              <input type="number" value={form.min_past_success} onChange={(e) => setForm({ ...form, min_past_success: Number(e.target.value) })} data-testid="rule-min-past"
                     className="w-full mt-0.5 px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-sm" />
            </label>
            <label className="text-[10px] font-black opacity-80">Note
              <input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Optional" data-testid="rule-note"
                     className="w-full mt-0.5 px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-sm" />
            </label>
          </div>
          <button onClick={create} data-testid="rule-create"
                  className="w-full py-2 rounded-lg bg-yellow-400 text-slate-900 font-black text-xs flex items-center justify-center gap-1">
            <Zap size={12} /> Create Rule
          </button>
        </div>

        <div className="space-y-1.5">
          {rules.length === 0 && <div className="text-center py-8 opacity-60 text-xs">No rules yet — create one above</div>}
          {rules.map((r) => (
            <div key={r.id} className="rounded-xl bg-slate-900/70 border border-slate-800 p-3 flex items-center gap-2" data-testid={`rule-row-${r.id.slice(0,8)}`}>
              <span className={`w-2 h-8 rounded-full ${r.active ? 'bg-emerald-500' : 'bg-slate-600'}`}></span>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-black">{r.kind === 'fund_deposit' ? '💰 Fund Deposit' : '🪙 AT Deposit'} ≤ ₹{r.max_amount}</div>
                <div className="text-[10px] opacity-70">
                  Auto-approve when user has ≥{r.min_past_success} past approved · {r.active ? '✅ ACTIVE' : '⏸️ Paused'}
                  {r.note && <span className="italic ml-1">· {r.note}</span>}
                </div>
              </div>
              <button onClick={() => toggle(r.id, r.active)} data-testid={`rule-toggle-${r.id.slice(0,8)}`}
                      className={`px-2 py-1 rounded text-[10px] font-black ${r.active ? 'bg-amber-500 text-slate-900' : 'bg-emerald-500 text-slate-900'} flex items-center gap-1`}>
                <Power size={10} /> {r.active ? 'Pause' : 'Activate'}
              </button>
              <button onClick={() => del(r.id)} data-testid={`rule-delete-${r.id.slice(0,8)}`}
                      className="px-2 py-1 rounded bg-rose-600 text-white text-[10px] font-black flex items-center gap-1">
                <Trash2 size={10} /> Delete
              </button>
            </div>
          ))}
        </div>
      </div>
    </AdminShell>
  );
}
